import { createSelector } from '@reduxjs/toolkit';
import { MOCK_QUESTION_SETS } from './chatSlice';

const selectChat = (state) => state.chat || {};

// Basic selectors
export const selectChatIsOpen = createSelector([selectChat], (chat) => chat.isOpen || false);
export const selectChatMessages = createSelector([selectChat], (chat) => chat.messages || []);
export const selectShowEmailForm = createSelector([selectChat], (chat) => chat.showEmailForm || false);
export const selectIsTyping = createSelector([selectChat], (chat) => chat.isTyping || false);

export const selectFormData = createSelector([selectChat], (chat) => chat.formData || {});
export const selectCollectedData = createSelector([selectChat], (chat) => chat.collectedData || []);
export const selectSubmissions = createSelector([selectChat], (chat) => chat.submissions || []);

// Question set selectors
export const selectCurrentQuestionSetId = createSelector([selectChat], (chat) => chat.currentQuestionSet || 'event_preferences');
export const selectCurrentQuestionIndex = createSelector([selectChat], (chat) => chat.currentQuestionIndex || 0);

// Get current question set
export const selectCurrentQuestionSet = createSelector(
  [selectCurrentQuestionSetId],
  (questionSetId) => MOCK_QUESTION_SETS[questionSetId] || MOCK_QUESTION_SETS.event_preferences
);

// Get current question
export const selectCurrentQuestion = createSelector(
  [selectCurrentQuestionSet, selectCurrentQuestionIndex],
  (questionSet, index) => questionSet.questions[index] || null
);

// Get all question sets
export const selectAllQuestionSets = () => MOCK_QUESTION_SETS;

// Get progress percentage
export const selectProgressPercentage = createSelector(
  [selectCurrentQuestionSet, selectCurrentQuestionIndex],
  (questionSet, index) => {
    if (!questionSet || !questionSet.questions) return 0;
    return ((index + 1) / questionSet.questions.length) * 100;
  }
);

// Get answer for specific question
export const selectAnswerForQuestion = (questionId) => 
  createSelector([selectCollectedData], (collectedData) => 
    collectedData.find(item => item.questionId === questionId)?.value || ''
  );

// Check if form is complete
export const selectIsFormComplete = createSelector(
  [selectCurrentQuestionSet, selectFormData],
  (questionSet, formData) => {
    if (!questionSet) return false;
    
    return questionSet.questions.every(question => {
      if (!question.required) return true;
      
      const value = formData[question.field];
      if (question.type === 'multi-select') {
        return Array.isArray(value) && value.length > 0;
      }
      return value && value.toString().trim();
    });
  }
);

// Get submission stats
export const selectSubmissionStats = createSelector(
  [selectSubmissions],
  (submissions) => {
    const total = submissions.length;
    const byQuestionSet = submissions.reduce((acc, submission) => {
      acc[submission.questionSetId] = (acc[submission.questionSetId] || 0) + 1;
      return acc;
    }, {});
    
    return {
      total,
      byQuestionSet,
      lastSubmission: submissions[submissions.length - 1]
    };
  }
);